import React, { useState } from 'react';
import { TruckIcon, MapPinIcon, PackageIcon, CalendarIcon, FileTextIcon, UserIcon, PhoneIcon, CheckIcon } from 'lucide-react';
interface ShipmentConfirmationProps {
  quoteData: {
    originZip: string;
    destinationZip: string;
    equipmentType: string;
    weight: string;
    pickupDate?: string;
    deliveryDate?: string;
    commodity?: string;
    specialRequirements: string[];
  };
  selectedQuote: {
    id: string;
    carrierId: string;
    carrierName: string;
    totalPrice: number;
    transitDays: number;
    services: string[];
  };
  onConfirm: () => void;
  onBack: () => void;
}
const ShipmentConfirmation: React.FC<ShipmentConfirmationProps> = ({
  quoteData,
  selectedQuote,
  onConfirm,
  onBack
}) => {
  const [additionalDetails, setAdditionalDetails] = useState({
    orderNumber: '',
    originContact: {
      name: '',
      phone: '',
      appointmentStart: '',
      appointmentEnd: '',
      remarks: ''
    },
    destinationContact: {
      name: '',
      phone: '',
      appointmentStart: '',
      appointmentEnd: '',
      remarks: ''
    },
    specialInstructions: ''
  });
  const handleInputChange = (field: string, value: string) => {
    setAdditionalDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleContactChange = (type: 'origin' | 'destination', field: string, value: string) => {
    setAdditionalDetails(prev => ({
      ...prev,
      [`${type}Contact`]: {
        ...prev[`${type}Contact`],
        [field]: value
      }
    }));
  };
  const isFormValid = () => {
    return additionalDetails.orderNumber && additionalDetails.originContact.name && additionalDetails.originContact.phone && additionalDetails.destinationContact.name && additionalDetails.destinationContact.phone;
  };
  return <div className="flex flex-col h-full" data-id="element-325">
      <div className="flex-1" data-id="element-326">
        {/* Required Fields Section */}
        <div className="bg-white rounded-lg shadow-sm p-3 mb-3" data-id="element-327">
          <h2 className="text-md font-medium text-gray-900 mb-3 flex items-center" data-id="element-328">
            <FileTextIcon size={18} className="mr-2 text-primary-700" data-id="element-329" />
            Required Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-330">
            <div data-id="element-331">
              <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-332">
                Order Number *
              </label>
              <input type="text" value={additionalDetails.orderNumber} onChange={e => handleInputChange('orderNumber', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" required data-id="element-333" />
            </div>
          </div>
        </div>
        {/* Contact Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3" data-id="element-334">
          {/* Origin Contact */}
          <div className="bg-white rounded-lg shadow-sm p-3" data-id="element-335">
            <h3 className="text-sm font-medium text-gray-900 mb-2 flex items-center" data-id="element-336">
              <UserIcon size={16} className="mr-2 text-primary-700" data-id="element-337" />
              Origin Details
            </h3>
            <div className="space-y-2" data-id="element-338">
              <div data-id="element-339">
                <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-340">
                  Contact Name *
                </label>
                <input type="text" value={additionalDetails.originContact.name} onChange={e => handleContactChange('origin', 'name', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" required data-id="element-341" />
              </div>
              <div data-id="element-342">
                <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-343">
                  Phone Number *
                </label>
                <input type="tel" value={additionalDetails.originContact.phone} onChange={e => handleContactChange('origin', 'phone', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" required data-id="element-344" />
              </div>
              <div data-id="element-345">
                <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-346">
                  Appointment Window
                </label>
                <div className="grid grid-cols-2 gap-2" data-id="element-347">
                  <input type="datetime-local" value={additionalDetails.originContact.appointmentStart} onChange={e => handleContactChange('origin', 'appointmentStart', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" placeholder="Start" data-id="element-348" />
                  <input type="datetime-local" value={additionalDetails.originContact.appointmentEnd} onChange={e => handleContactChange('origin', 'appointmentEnd', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" placeholder="End" data-id="element-349" />
                </div>
              </div>
              <div data-id="element-350">
                <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-351">
                  Remarks
                </label>
                <textarea value={additionalDetails.originContact.remarks} onChange={e => handleContactChange('origin', 'remarks', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" rows={2} placeholder="Any special instructions or notes" data-id="element-352" />
              </div>
            </div>
          </div>
          {/* Destination Contact */}
          <div className="bg-white rounded-lg shadow-sm p-3" data-id="element-353">
            <h3 className="text-sm font-medium text-gray-900 mb-2 flex items-center" data-id="element-354">
              <UserIcon size={16} className="mr-2 text-primary-700" data-id="element-355" />
              Destination Details
            </h3>
            <div className="space-y-2" data-id="element-356">
              <div data-id="element-357">
                <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-358">
                  Contact Name *
                </label>
                <input type="text" value={additionalDetails.destinationContact.name} onChange={e => handleContactChange('destination', 'name', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" required data-id="element-359" />
              </div>
              <div data-id="element-360">
                <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-361">
                  Phone Number *
                </label>
                <input type="tel" value={additionalDetails.destinationContact.phone} onChange={e => handleContactChange('destination', 'phone', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" required data-id="element-362" />
              </div>
              <div data-id="element-363">
                <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-364">
                  Appointment Window
                </label>
                <div className="grid grid-cols-2 gap-2" data-id="element-365">
                  <input type="datetime-local" value={additionalDetails.destinationContact.appointmentStart} onChange={e => handleContactChange('destination', 'appointmentStart', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" placeholder="Start" data-id="element-366" />
                  <input type="datetime-local" value={additionalDetails.destinationContact.appointmentEnd} onChange={e => handleContactChange('destination', 'appointmentEnd', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" placeholder="End" data-id="element-367" />
                </div>
              </div>
              <div data-id="element-368">
                <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-369">
                  Remarks
                </label>
                <textarea value={additionalDetails.destinationContact.remarks} onChange={e => handleContactChange('destination', 'remarks', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" rows={2} placeholder="Any special instructions or notes" data-id="element-370" />
              </div>
            </div>
          </div>
        </div>
        {/* Quote Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3" data-id="element-371">
          {/* Route & Equipment */}
          <div className="bg-white rounded-lg shadow-sm p-3" data-id="element-372">
            <h3 className="text-sm font-medium text-gray-900 mb-2 flex items-center" data-id="element-373">
              <TruckIcon size={16} className="mr-2 text-primary-700" data-id="element-374" />
              Route & Equipment
            </h3>
            <div className="grid grid-cols-2 gap-2 text-sm" data-id="element-375">
              <div data-id="element-376">
                <p className="text-xs text-gray-500" data-id="element-377">Origin</p>
                <p className="text-gray-900" data-id="element-378">{quoteData.originZip}</p>
              </div>
              <div data-id="element-379">
                <p className="text-xs text-gray-500" data-id="element-380">Destination</p>
                <p className="text-gray-900" data-id="element-381">{quoteData.destinationZip}</p>
              </div>
              <div data-id="element-382">
                <p className="text-xs text-gray-500" data-id="element-383">Equipment</p>
                <p className="text-gray-900" data-id="element-384">{quoteData.equipmentType}</p>
              </div>
              <div data-id="element-385">
                <p className="text-xs text-gray-500" data-id="element-386">Weight</p>
                <p className="text-gray-900" data-id="element-387">{quoteData.weight} lbs</p>
              </div>
            </div>
          </div>
          {/* Rate & Schedule */}
          <div className="bg-white rounded-lg shadow-sm p-3" data-id="element-388">
            <h3 className="text-sm font-medium text-gray-900 mb-2 flex items-center" data-id="element-389">
              <CalendarIcon size={16} className="mr-2 text-primary-700" data-id="element-390" />
              Rate & Schedule
            </h3>
            <div className="grid grid-cols-2 gap-2 text-sm" data-id="element-391">
              <div data-id="element-392">
                <p className="text-xs text-gray-500" data-id="element-393">Carrier</p>
                <p className="text-gray-900" data-id="element-394">{selectedQuote.carrierName}</p>
              </div>
              <div data-id="element-395">
                <p className="text-xs text-gray-500" data-id="element-396">Total Rate</p>
                <p className="font-medium text-gray-900" data-id="element-397">
                  ${selectedQuote.totalPrice.toLocaleString()}
                </p>
              </div>
              <div data-id="element-398">
                <p className="text-xs text-gray-500" data-id="element-399">Transit Time</p>
                <p className="text-gray-900" data-id="element-400">
                  {selectedQuote.transitDays} days
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Action Buttons - Fixed at bottom */}
      <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex justify-between mt-3 -mx-4 -mb-4" data-id="element-401">
        <button onClick={onBack} className="px-4 py-2 border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-402">
          Back
        </button>
        <button onClick={onConfirm} disabled={!isFormValid()} className="px-4 py-2 bg-primary-700 text-white rounded text-sm font-medium hover:bg-primary-800 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center" data-id="element-403">
          <CheckIcon size={16} className="mr-2" data-id="element-404" />
          Confirm Booking
        </button>
      </div>
    </div>;
};
export default ShipmentConfirmation;